import React from 'react';
import Icon from '../../../components/AppIcon';

const RewardsAnalytics = ({ rewards, redemptions }) => {
  const totalRedemptions = redemptions?.length;
  const pendingCount = redemptions?.filter(r => r?.status === 'pending')?.length;
  const completedCount = redemptions?.filter(r => r?.status === 'completed')?.length;
  const totalStarsSpent = redemptions?.reduce((sum, r) => sum + (r?.starsSpent || 0), 0);

  // Most popular rewards
  const rewardCounts = {};
  redemptions?.forEach(r => {
    const id = r?.rewardId;
    if (id) rewardCounts[id] = (rewardCounts?.[id] || 0) + 1;
  });

  const popularRewards = rewards?.map(r => ({ ...r, redemptionCount: rewardCounts?.[r?.id] || 0 }))?.sort((a, b) => b?.redemptionCount - a?.redemptionCount)?.slice(0, 5);

  // Category breakdown
  const categoryBreakdown = {};
  redemptions?.forEach(r => {
    const cat = r?.reward?.category || 'general';
    categoryBreakdown[cat] = (categoryBreakdown?.[cat] || 0) + 1;
  });

  const stats = [
    { label: 'Total Rewards', value: rewards?.length, icon: 'Gift', color: 'bg-amber-100 text-amber-600' },
    { label: 'Total Redemptions', value: totalRedemptions, icon: 'ShoppingBag', color: 'bg-blue-100 text-blue-600' },
    { label: 'Pending Approval', value: pendingCount, icon: 'Clock', color: 'bg-yellow-100 text-yellow-600' },
    { label: 'Stars Redeemed', value: totalStarsSpent, icon: 'Star', color: 'bg-orange-100 text-orange-600' },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats?.map(stat => (
          <div key={stat?.label} className="bg-white rounded-xl shadow-sm border border-border p-4">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${stat?.color}`}>
              <Icon name={stat?.icon} size={20} />
            </div>
            <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
            <p className="text-xs text-muted-foreground mt-0.5">{stat?.label}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Popular Rewards */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="TrendingUp" size={18} className="text-amber-500" />
            <span>Most Popular Rewards</span>
          </h3>
          {popularRewards?.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No redemptions yet</p>
          ) : (
            <div className="space-y-3">
              {popularRewards?.map((reward, idx) => (
                <div key={reward?.id} className="flex items-center space-x-3">
                  <span className="text-sm font-bold text-muted-foreground w-5">{idx + 1}</span>
                  <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Icon name={reward?.icon || 'Gift'} size={16} className="text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{reward?.title}</p>
                    <p className="text-xs text-muted-foreground">{reward?.redemptionCount} redemption{reward?.redemptionCount !== 1 ? 's' : ''}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <span className="text-sm">⭐</span>
                    <span className="text-sm font-medium text-amber-600">{reward?.starCost}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        <div className="bg-white rounded-xl shadow-sm border border-border p-5">
          <h3 className="font-semibold text-foreground mb-4 flex items-center space-x-2">
            <Icon name="PieChart" size={18} className="text-blue-500" />
            <span>Redemptions by Category</span>
          </h3>
          {Object.keys(categoryBreakdown)?.length === 0 ? (
            <p className="text-muted-foreground text-sm text-center py-6">No redemptions yet</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(categoryBreakdown)?.map(([cat, count]) => {
                const pct = totalRedemptions > 0 ? Math.round((count / totalRedemptions) * 100) : 0;
                return (
                  <div key={cat}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="font-medium text-foreground capitalize">{cat}</span>
                      <span className="text-muted-foreground">{count} ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-400 rounded-full transition-all duration-500"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RewardsAnalytics;
